import type { MonitoringOverview } from '../../../services/dmMonitoringOverview';
import { ActivityCard } from './ActivityCard';
import { AssigneeWorkloadCard } from './AssigneeWorkloadCard';
import { BacklogAgeCard } from './BacklogAgeCard';
import { EventTypesCard } from './EventTypesCard';
import { HandledPerPersonCard } from './HandledPerPersonCard';
import { LevelsCard } from './LevelsCard';
import { MessagesByFrustrationCard, MessagesByPriorityCard } from './MessageTriageCards';
import { OpenBacklogCard } from './OpenBacklogCard';
import { PAIR_ROW_CLASS, PIE_ROW_CLASS } from './overviewLayout';
import { OverviewKpiRow } from './OverviewKpiRow';
import type { HistoryView } from './overviewPeriod';
import { LivePairSkeleton, OverviewErrorState, PieRowSkeleton } from './OverviewStates';
import { ResolvedCard } from './ResolvedCard';
import { VaFlowCard } from './VaFlowCard';
import { VaMixCard } from './VaMixCard';

interface LiveRowProps {
    overview: MonitoringOverview;
    history: HistoryView;
}

function BreakdownRow({ overview, history }: LiveRowProps) {
    return (
        <div className={PIE_ROW_CLASS}>
            <LevelsCard levels={overview.levels} history={history} />
            <EventTypesCard eventTypes={overview.eventTypes} history={history} />
            <MessagesByPriorityCard priority={overview.triage.priority} />
            <MessagesByFrustrationCard frustration={overview.triage.frustration} />
        </div>
    );
}

function WorkloadRow({ overview }: Pick<LiveRowProps, 'overview'>) {
    return (
        <div className={PAIR_ROW_CLASS}>
            <BacklogAgeCard buckets={overview.age.buckets} />
            <AssigneeWorkloadCard assignees={overview.assignees} />
        </div>
    );
}

interface MonitoringOverviewContentProps {
    // Null until the live overview first loads; the range and trailing cards don't wait for it.
    overview: MonitoringOverview | null;
    liveFailed: boolean;
    onRetryLive: () => void;
    history: HistoryView;
}

export function MonitoringOverviewContent(props: MonitoringOverviewContentProps) {
    const { overview, liveFailed, onRetryLive, history } = props;
    return (
        <div className="flex flex-col gap-4">
            <OverviewKpiRow overview={overview} liveFailed={liveFailed} history={history} />
            {overview ? (
                <BreakdownRow overview={overview} history={history} />
            ) : liveFailed ? (
                <OverviewErrorState onRetry={onRetryLive} />
            ) : (
                <PieRowSkeleton />
            )}
            <div className={PAIR_ROW_CLASS}>
                <HandledPerPersonCard category="notification" />
                <HandledPerPersonCard category="message" />
            </div>
            <div className={PAIR_ROW_CLASS}>
                <ActivityCard history={history} />
                <OpenBacklogCard />
            </div>
            {overview ? (
                <WorkloadRow overview={overview} />
            ) : liveFailed ? null : (
                <LivePairSkeleton />
            )}
            <div className={PAIR_ROW_CLASS}>
                <VaFlowCard />
                <VaMixCard />
            </div>
            <ResolvedCard history={history} />
        </div>
    );
}
